import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import './dashboardV2.css';

import { dashboardV2QueryKeys } from './api/queryKeys';
import {
  DashboardFilterValues,
  dashboardFilterCount,
  EMPTY_FILTERS,
  EMPTY_QUEUE_FILTERS,
  QUEUE_PER_PAGE,
  QueueFilterValues,
  SummaryCounts,
} from './api/types';
import TabBar, { DashboardTab } from './components/TabBar';
import useDashboardSummary from './hooks/useDashboardSummary';
import CalendarTab from './tabs/CalendarTab';
import MissingTimesheetTab from './tabs/MissingTimesheetTab';
import NeedsApprovalTab from './tabs/NeedsApprovalTab';
import NoShowTab from './tabs/NoShowTab';

import { LtaGigMenu } from '@/components/LtaGigMenu';
import Page from '@/components/Page';
import useAuth from '@/hooks/useAuth';
import useOrderFlowV2Enabled from '@/hooks/useOrderFlowV2Enabled';
import { JobTypeEnum } from '@/types/graphql';
import {
  DashboardV2Feature,
  trackDashboardV2FeatureUsed,
  trackDashboardV2TabViewed,
  trackDashboardV2Viewed,
} from '@/util/vexo';

// Dashboard V2 — the risk-surfacing ops dashboard (GravyWork 2.0 Baseline).
// New, self-contained page mounted at /dashboard-v2; the legacy dashboard at
// "/" is untouched. Data flows over REST only (see ./api). This phase builds
// the Calendar tab pixel-perfect plus the tab bar with live count badges; the
// three queue tabs are stubs whose counts are already live.
//
// The app shell (sidebar + Page header with title/Place Order) is reused as-is
// per the "dashboard content only" scope. Place Order mirrors the legacy
// dashboard's wiring so order creation behaves identically.
// Kept next to the union so an unknown ?tab= value falls back to Calendar
// rather than rendering nothing.
const TABS: DashboardTab[] = [
  'calendar',
  'no_show',
  'missing_timesheet',
  'needs_approval',
];

// The three action queues — Calendar has its own filter control.
type QueueName = 'no_show' | 'missing_timesheet' | 'needs_approval';

// Which calendar filter dimension maps to which tracked feature. Only the
// dimensions Ops can actually choose are listed — `customerIds` and
// `accountIds` are two renderings of the same "which client" control (only one
// ever renders, depending on admin type), so both report `filter_client`
// rather than splitting one behaviour across two metrics.
const CALENDAR_FILTER_FEATURES: [
  keyof DashboardFilterValues,
  DashboardV2Feature,
][] = [
  ['customerIds', 'filter_client'],
  ['accountIds', 'filter_client'],
  ['skillIds', 'filter_skill'],
  ['regionIds', 'filter_region'],
  ['status', 'filter_status'],
  ['fillStatus', 'filter_status'],
];

// Badge count for the tab being reported on, for `dashboard_v2_tab_viewed`.
// Calendar has no badge (it is not a queue), and the summary may not have
// resolved yet on first paint — both cases return null rather than 0, because
// "no count exists" and "the queue is empty" are different facts and the
// rollup must not average them together.
const tabItemCount = (
  tab: DashboardTab,
  summary?: SummaryCounts,
): number | null => {
  if (tab === 'calendar' || !summary) return null;
  const count = summary[tab as QueueName];
  return typeof count === 'number' ? count : null;
};

// Every piece of view state on this page lives HERE, above the tab switch,
// because only the active tab is mounted (GRAVY-10303 / 10304 / 10321). State
// held inside a tab is destroyed the moment the admin looks at another one, so
// anything the admin chose and expects to come back to has to be owned above the
// thing that unmounts. The week is the exception, and only because it is already
// durable elsewhere: `useWeekNav` keeps it in `?sd=`.

const DashboardV2 = () => {
  const { currentAgency, currentAdmin, isCurrentCustomerAdmin } = useAuth();
  const agencyId = currentAgency?.id ? Number(currentAgency.id) : null;

  const orderFlowV2Enabled = useOrderFlowV2Enabled();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // The active tab lives in the URL, not component state, so leaving the page
  // and coming back — browser Back from a resolution flow, a refresh, or a
  // shared link — returns to the queue the admin was working, instead of
  // silently resetting to Calendar and making them find their place again
  // (GRAVY-10297).
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: DashboardTab = TABS.includes(tabParam as DashboardTab)
    ? (tabParam as DashboardTab)
    : 'calendar';

  // Calendar's own filter set, and whether its bar is revealed (GRAVY-10321).
  // These were CalendarTab's local state, which meant a glance at any action
  // queue silently discarded them — the one dimension of this page that did not
  // already survive a tab switch, and the reason Ops had to rebuild a filter
  // every time they checked a queue.
  //
  // Kept separate from `queueFilters`: Calendar filters on six dimensions the
  // queues do not have, and a Calendar selection must never narrow a queue.
  const [calendarFilters, setCalendarFilters] =
    useState<DashboardFilterValues>(EMPTY_FILTERS);
  // Revealed-ness is preserved for the same reason the selections are: an admin
  // who opened the bar, checked a queue and came back should not have to open it
  // again to see what is applied. Reset filters — not a tab switch — is the way
  // to clear the selection.
  const [calendarFiltersOpen, setCalendarFiltersOpen] = useState(false);

  // One filter set per action queue, held HERE rather than inside each tab:
  // only the active tab is mounted, so tab-local state is destroyed the moment
  // the admin looks at another queue and their selections would be silently
  // lost on the way back (GRAVY-10303). Three separate objects, so no queue can
  // narrow another.
  const [queueFilters, setQueueFilters] = useState<
    Record<QueueName, QueueFilterValues>
  >({
    no_show: EMPTY_QUEUE_FILTERS,
    missing_timesheet: EMPTY_QUEUE_FILTERS,
    needs_approval: EMPTY_QUEUE_FILTERS,
  });

  // The page number lives here for the same reason the filters do: only the
  // active tab is mounted, so page state held inside a tab would reset to 1
  // every time the admin looked at another queue (GRAVY-10304).
  const [queuePages, setQueuePages] = useState<Record<QueueName, number>>({
    no_show: 1,
    missing_timesheet: 1,
    needs_approval: 1,
  });

  // Calendar filter changes, wrapped so each dimension the admin actually
  // touched is reported once. Emitted from the setter rather than from each
  // pill, because the pills are shared components and instrumenting them
  // individually would double-count when a pill is reused elsewhere.
  //
  // Diffed against the previous value so a re-render carrying an unchanged
  // filter set emits nothing — otherwise `dashboard.feature_uses` would
  // measure renders instead of use.
  //
  // The diff runs on the CURRENT value rather than inside the setState
  // updater. An updater must be a pure function — React invokes it twice under
  // StrictMode — so tracking from inside it would double every filter metric
  // in development and, with future concurrent rendering, in production too.
  const setCalendarFiltersTracked = useCallback(
    (next: DashboardFilterValues) => {
      const prev = calendarFilters;
      if (dashboardFilterCount(next) === 0 && dashboardFilterCount(prev) > 0) {
        trackDashboardV2FeatureUsed({
          feature: 'filter_reset',
          tab: 'calendar',
        });
      } else {
        // De-duplicated: `customerIds` and `accountIds` both map to
        // filter_client, and changing one must not report the control twice.
        const seen = new Set<DashboardV2Feature>();
        CALENDAR_FILTER_FEATURES.forEach(([key, feature]) => {
          if (prev[key].length !== next[key].length && !seen.has(feature)) {
            seen.add(feature);
            trackDashboardV2FeatureUsed({ feature, tab: 'calendar' });
          }
        });
      }
      setCalendarFilters(next);
    },
    [calendarFilters],
  );

  // Changing a filter resets that queue to page 1. Staying on page 4 of a
  // narrower result set would land the admin on an empty page and read as
  // "the filter returned nothing".
  const setQueueFilter = useCallback(
    (queue: QueueName) => (next: QueueFilterValues) => {
      trackDashboardV2FeatureUsed({ feature: 'filter_client', tab: queue });
      setQueueFilters((prev) => ({ ...prev, [queue]: next }));
      setQueuePages((prev) => ({ ...prev, [queue]: 1 }));
    },
    [],
  );

  // One STABLE setter per queue, memoized rather than built per render.
  //
  // A fresh closure each render is fine for a click handler, but the queues now
  // also correct their own page from an effect when live results shrink
  // (`useQueuePageClamp`, GRAVY-10323) — and an unstable callback in that
  // effect's dependencies would re-run it on every render. Identity is part of
  // the contract now, so it is pinned here.
  const setQueuePages_ = useMemo(
    () =>
      ({
        no_show: (next: number) =>
          setQueuePages((prev) => ({ ...prev, no_show: next })),
        missing_timesheet: (next: number) =>
          setQueuePages((prev) => ({ ...prev, missing_timesheet: next })),
        needs_approval: (next: number) =>
          setQueuePages((prev) => ({ ...prev, needs_approval: next })),
      }) as Record<QueueName, (next: number) => void>,
    [],
  );

  const setQueuePage = useCallback(
    (queue: QueueName) => setQueuePages_[queue],
    [setQueuePages_],
  );

  // The three queues share one page size, so each tab is handed a whole
  // QueuePage rather than assembling one itself.
  const queuePage = (queue: QueueName) => ({
    page: queuePages[queue],
    perPage: QUEUE_PER_PAGE,
  });

  const summary = useDashboardSummary(agencyId as number);
  const queryClient = useQueryClient();

  // Selecting a tab invalidates every Dashboard V2 query, so the badge and the
  // list it belongs to revalidate together. Without this the badge (mounted at
  // page level, so it never remounts) and a queue list (remounted per tab
  // switch) refresh on independent schedules and can disagree.
  const onTabChange = useCallback(
    (tab: DashboardTab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          // Calendar is the default, so it stays out of the URL and the plain
          // /dashboard-v2 link keeps working.
          if (tab === 'calendar') next.delete('tab');
          else next.set('tab', tab);
          return next;
        },
        // Switching tabs is not a navigation step — Back should leave the
        // dashboard, not walk through every tab the admin tried.
        { replace: true },
      );
      queryClient.invalidateQueries({ queryKey: dashboardV2QueryKeys.root });
    },
    [queryClient, setSearchParams],
  );

  // ── Analytics ──────────────────────────────────────────────────────────
  //
  // Dashboard V2 shipped with no instrumentation at all, so nothing could say
  // whether the quarter's biggest delivery was used. These two effects supply
  // the `dashboard.*` rollups (graphy: Analytics::Rollup::DashboardService).
  //
  // Fired once per mount rather than on every tab change: `dashboard.sessions`
  // must count admins who OPENED the dashboard, and it is the denominator of
  // `dashboard.action_rate`. Firing it per tab would inflate the denominator
  // and make a surface people use heavily look like one they bounce off.
  useEffect(() => {
    trackDashboardV2Viewed({ tab: activeTab });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Per-tab reach, including the tab the page opened on. `item_count` travels
  // with it because a queue that is always empty is used differently from one
  // that is always full, and reach alone cannot tell those apart.
  useEffect(() => {
    trackDashboardV2TabViewed({
      tab: activeTab,
      item_count: tabItemCount(activeTab, summary.data),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const onPlaceOrder = () => {
    if (
      isCurrentCustomerAdmin(currentAdmin) &&
      !currentAdmin.customer?.ltaAllowed
    ) {
      navigate(
        orderFlowV2Enabled
          ? `/orders-v2/create?type=${JobTypeEnum.GIG}`
          : `/orders/creategigorder?type=${JobTypeEnum.GIG}`,
      );
    } else {
      setAnchorEl(menuRef?.current);
    }
  };

  return (
    <Page
      title="Dashboard"
      size="xl"
      primaryAction={{
        a11yLabel: 'Go to create order',
        label: 'Place Order',
        onAction: onPlaceOrder,
        id: 'dashv2-place-order',
        ref: menuRef,
      }}
    >
      <LtaGigMenu
        anchorEl={anchorEl}
        handleCloseMenu={() => setAnchorEl(null)}
        open={Boolean(anchorEl)}
        useV2={orderFlowV2Enabled}
      />

      <div className="dashv2">
        {/* "Ops on duty today" (Figma `ops-sched`) is deliberately NOT mounted:
            it belongs to a future ticket, and graphy has no ops rota to feed it.
            The component, its styles and its tests are built and kept in
            `components/OpsOnDuty.tsx` — mounting it is a one-line change once
            that ticket lands. */}

        <TabBar
          active={activeTab}
          onChange={onTabChange}
          counts={summary.data}
        />

        {agencyId == null ? null : activeTab === 'calendar' ? (
          <CalendarTab
            agencyId={agencyId}
            filters={calendarFilters}
            onFiltersChange={setCalendarFiltersTracked}
            filtersOpen={calendarFiltersOpen}
            onFiltersOpenChange={setCalendarFiltersOpen}
          />
        ) : activeTab === 'no_show' ? (
          <NoShowTab
            agencyId={agencyId}
            filters={queueFilters.no_show}
            onFiltersChange={setQueueFilter('no_show')}
            page={queuePage('no_show')}
            onPageChange={setQueuePage('no_show')}
          />
        ) : activeTab === 'missing_timesheet' ? (
          <MissingTimesheetTab
            agencyId={agencyId}
            filters={queueFilters.missing_timesheet}
            onFiltersChange={setQueueFilter('missing_timesheet')}
            page={queuePage('missing_timesheet')}
            onPageChange={setQueuePage('missing_timesheet')}
          />
        ) : (
          <NeedsApprovalTab
            agencyId={agencyId}
            filters={queueFilters.needs_approval}
            onFiltersChange={setQueueFilter('needs_approval')}
            page={queuePage('needs_approval')}
            onPageChange={setQueuePage('needs_approval')}
          />
        )}
      </div>
    </Page>
  );
};

export default DashboardV2;
